import { Box, Typography, SxProps, alpha } from '@mui/material';
import { ReactNode } from 'react';
import { BrandColors, NeutralColors } from '@neumind/shared/branding';

export interface PhoneNotificationProps {
    /** Small informative glyph shown in the app-icon chip. */
    icon: ReactNode;
    /** Bold notification title, e.g. "Fatigue check-in". */
    title: string;
    /** One-line notification body. */
    body: string;
    /** Relative timestamp, e.g. "now", "9:30". */
    time?: string;
    /** App name shown in the header row. */
    appName?: string;
    /** Accent colour for the icon chip. */
    accentColor?: string;
    sx?: SxProps;
}

/**
 * Phone-OS-style notification card (IDEA-260721-rpva item 1).
 *
 * Replaces the old floating screenshot cards with the visual language of a
 * real lock-screen notification: frosted glass, app-icon chip, app name +
 * timestamp row, bold title, single-line body. Pure code (no image asset), so
 * copy stays editable and the cards render crisp at any DPR.
 */
export function PhoneNotification({
    icon,
    title,
    body,
    time = 'now',
    appName = 'neumind',
    accentColor = BrandColors.primary.main,
    sx,
}: PhoneNotificationProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                p: 1.5,
                borderRadius: 3,
                // Frosted-glass surface over whatever sits behind the card.
                bgcolor: alpha(NeutralColors[50], 0.85),
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                border: '1px solid rgba(255,255,255,0.65)',
                boxShadow: '0 8px 24px -6px rgba(27,58,107,0.18), 0 2px 6px -2px rgba(27,58,107,0.10)',
                textAlign: 'left',
                ...sx,
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${accentColor} 0%, ${alpha(accentColor, 0.75)} 100%)`,
                    '& svg': { fontSize: 20 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            flexGrow: 1,
                        }}
                    >
                        {appName}
                    </Typography>
                    {time && (
                        <Typography
                            variant="caption"
                            sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}
                        >
                            {time}
                        </Typography>
                    )}
                </Box>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.4 }}
                >
                    {body}
                </Typography>
            </Box>
        </Box>
    );
}
